import { Button, Card, Divider, Flex, Tag, Typography } from 'antd';
import { useLiveQuery } from 'dexie-react-hooks';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import {
  isModuleEnabled,
  usePermissions,
} from 'business/contextProviders/usePermissions';
import { DefectStatus } from 'business/quality-control/pages/record-segment-defect/components/left-panel/board-actions/types';
import { useBoardContext } from 'business/quality-control/pages/record-segment-defect/context/board-context';
import { matchesReportStateFilters } from 'business/quality-control/pages/record-segment-defect/utils/matches-report-state-filters';
import { Module_Enum } from 'generated/graphql';
import { db } from 'technical/db';

import styles from './index.module.scss';

export const DefectListDetail = ({
  selectedRing,
  isRingCompliant,
}: {
  selectedRing: string;
  isRingCompliant: boolean;
}) => {
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site id');

  const { t } = useTranslation();
  const {
    defectFilter,
    defectReportStateFilter,
    repairReportStateFilter,
    defectId,
    setDefectId,
  } = useBoardContext();
  const { extraQualityPermissions, isDataManager } = usePermissions();

  const defects = useLiveQuery(async () => {
    const defectReports = await db.defectReports
      .where('ringId')
      .equals(selectedRing)
      .and((defectReport) => defectReport.deletedAt === null)
      .sortBy('submittedAt');

    const [repairReports, defectReportTypes] = await Promise.all([
      db.repairReports
        .where('defectReportId')
        .anyOf(defectReports.map((defect) => defect.id))
        .and((repairReport) => repairReport.deletedAt === null)
        .toArray(),
      db.defectReportTypes.toArray(),
    ]);

    return defectReports.map((defect) => {
      return {
        id: defect.id,
        segmentType: defect.segmentType,
        serialNumber: defect.serialNumber,
        defectTypeLabel:
          defectReportTypes.find(
            (defectType) => defectType.id === defect.defectReportTypeId,
          )?.label ?? '—',
        repair: repairReports.find(
          (repair) => repair?.defectReportId === defect.id,
        ),
        toRepair: defect.toRepair,
        submittedAt: defect.submittedAt,
      };
    });
  }, [selectedRing, defectId]);

  const visibleDefects = defects?.filter((defect) => {
    const matchesDefectFilter =
      defectFilter !== DefectStatus.ToRepair || defect.toRepair;
    const matchesReportState = matchesReportStateFilters({
      defectReportStateFilter,
      repairReportStateFilter,
      defectSubmittedAt: defect.submittedAt,
      repairSubmittedAt: defect.repair?.submittedAt,
    });
    return matchesDefectFilter && matchesReportState;
  });

  const getTagColor = (defect: NonNullable<typeof visibleDefects>[number]) => {
    if (isRingCompliant) {
      return {
        color: '#2966FF',
        backgroundColor: '#D5EAFF',
        borderColor: '#C0DBF6',
        borderRadius: 10,
      };
    }

    if (defect.repair?.submittedAt != null) {
      return {
        color: '#01AB61',
        backgroundColor: '#DDFFE4',
        borderColor: '#C9FAD8',
        borderRadius: 10,
      };
    }

    if (!defect.submittedAt || (defect.repair && !defect.repair.submittedAt)) {
      return {
        color: '#666666',
        backgroundColor: '#EDEDED',
        borderColor: '#D9D9D9',
        borderRadius: 10,
      };
    }

    return {
      color: '#C67402',
      backgroundColor: '#FFF8DD',
      borderColor: '#F5E9BE',
      borderRadius: 10,
    };
  };

  return (
    <Card
      title={t(
        'qualityControl.recordSegmentDefect.rightPanel.ringDetails.defectListDetail.title',
      )}
      size="small"
      variant="borderless"
      className={styles.card}
      classNames={{ header: styles.cardHeader, body: styles.listBody }}
    >
      <Flex vertical gap={8}>
        <Button
          type="primary"
          className={styles.defectButton}
          onClick={async () => {
            const id = crypto.randomUUID();
            setDefectId(id);
          }}
          disabled={isRingCompliant || !extraQualityPermissions.canEditReport}
        >
          {t(
            'qualityControl.recordSegmentDefect.rightPanel.ringDetails.defectListDetail.newDefect',
          )}
        </Button>
        <div className={styles.listContainer}>
          <Flex vertical>
            {visibleDefects?.map((defect, index) => {
              return (
                <div key={defect.id}>
                  <Flex
                    justify="space-between"
                    align="center"
                    className={styles.item}
                  >
                    <Typography.Text>{defect.segmentType}</Typography.Text>
                    {isModuleEnabled(
                      isDataManager,
                      currentConstructionSite,
                      Module_Enum.QualityScan,
                    ) ? (
                      <Typography.Text className={styles.serialNumber}>
                        {defect.serialNumber ?? '-'}
                      </Typography.Text>
                    ) : null}
                    <Tag style={getTagColor(defect)}>
                      <span>&#x25CF; </span>
                      {defect.repair
                        ? t(
                            'qualityControl.recordSegmentDefect.rightPanel.ringDetails.defectListDetail.repaired',
                          )
                        : t(
                            'qualityControl.recordSegmentDefect.rightPanel.ringDetails.defectListDetail.defect',
                          )}
                    </Tag>
                    <Button onClick={() => setDefectId(defect.id)}>
                      {t(
                        'qualityControl.recordSegmentDefect.rightPanel.ringDetails.defectListDetail.details',
                      )}
                    </Button>
                  </Flex>
                  {index < (visibleDefects?.length ?? 0) - 1 && (
                    <Divider style={{ margin: 0 }} />
                  )}
                </div>
              );
            })}
          </Flex>
        </div>
      </Flex>
    </Card>
  );
};
